import React, { useState, useEffect } from 'react'
import { useAuth } from '../../../utils/firebase/auth'
import { Alert } from '../../../components'
import RoleInfoSection from './RoleInfoSection'
import RoleRequestSection from './RoleRequestSection'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPhone } from '@fortawesome/free-solid-svg-icons'

function Profile() {
  const { authUser, userProfile } = useAuth()
  const [loading, setLoading] = useState(false)
  const [roleStatus, setRoleStatus] = useState({
    currentStatus: 'STUDENT', // Default status
    pendingApproval: false,
    pendingRoleId: '',
  })

  // WhatsApp linking state
  const [whatsappLinking, setWhatsappLinking] = useState({
    isLinked: false,
    isLinking: false,
    phoneNumber: '',
    verificationCode: '',
    error: null,
  })

  useEffect(() => {
    // Update role status when userProfile changes
    if (userProfile?.roles) {
      // Find the highest role the user has
      const roleHierarchy = [
        'CORE',
        'AMBASSADOR',
        'STUDENT_AMBASSADOR',
        'STUDENT',
      ]
      const currentRole = roleHierarchy.find(
        (role) => userProfile.roles[role.toLowerCase()]
      )

      setRoleStatus((prev) => ({
        ...prev,
        currentStatus: currentRole || 'STUDENT',
      }))
    }

    // Check WhatsApp linking status
    if (userProfile?.whatsappLinked) {
      setWhatsappLinking((prev) => ({
        ...prev,
        isLinked: true,
        phoneNumber: userProfile.whatsappNumber,
      }))
    }
  }, [userProfile])

  const handleRequestRole = async (roleId) => {
    try {
      setLoading(true)
      // Make API call to request role
      const response = await fetch('/api/roles/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: authUser?.uid,
          requestedRole: roleId,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to request role')
      }

      setRoleStatus((prev) => ({
        ...prev,
        pendingApproval: true,
        pendingRoleId: roleId,
      }))

      Alert.fire({
        icon: 'success',
        title: 'Role Request Submitted',
        text: 'Your request has been submitted for review.',
      })
    } catch (error) {
      Alert.fire({
        icon: 'error',
        title: 'Request Failed',
        text: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCancelRequest = async () => {
    try {
      setLoading(true)
      // Make API call to cancel role request
      const response = await fetch('/api/roles/cancel-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: authUser?.uid,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to cancel role request')
      }

      setRoleStatus((prev) => ({
        ...prev,
        pendingApproval: false,
        pendingRoleId: '',
      }))

      Alert.fire({
        icon: 'success',
        title: 'Request Cancelled',
        text: 'Your role request has been cancelled.',
      })
    } catch (error) {
      Alert.fire({
        icon: 'error',
        title: 'Cancellation Failed',
        text: error.message,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleWhatsAppLinking = async () => {
    try {
      setWhatsappLinking((prev) => ({ ...prev, isLinking: true, error: null }))

      const response = await fetch('/api/whatsapp/link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: whatsappLinking.phoneNumber,
          email: authUser?.email,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Failed to start WhatsApp linking' }))
        throw new Error(errorData.error || 'Failed to start WhatsApp linking')
      }

      Alert.fire({
        icon: 'success',
        title: 'Check Your Email',
        text: 'Please check your email for the verification code.',
      })
    } catch (error) {
      const errorMessage = error.message || 'An unexpected error occurred'
      setWhatsappLinking((prev) => ({ ...prev, error: errorMessage }))
      Alert.fire({
        icon: 'error',
        title: 'WhatsApp Link Failed',
        text: errorMessage,
      })
    } finally {
      setWhatsappLinking((prev) => ({ ...prev, isLinking: false }))
    }
  }

  const verifyWhatsAppCode = async () => {
    try {
      setWhatsappLinking((prev) => ({ ...prev, isLinking: true, error: null }))

      const response = await fetch('/api/whatsapp/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: whatsappLinking.verificationCode,
          phoneNumber: whatsappLinking.phoneNumber,
        }),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Invalid verification code' }))
        throw new Error(errorData.error || 'Invalid verification code')
      }

      setWhatsappLinking((prev) => ({
        ...prev,
        isLinked: true,
        isLinking: false,
        error: null,
      }))

      Alert.fire({
        icon: 'success',
        title: 'WhatsApp Linked',
        text: 'WhatsApp successfully linked to your account!',
      })
    } catch (error) {
      const errorMessage = error.message || 'An unexpected error occurred'
      setWhatsappLinking((prev) => ({ ...prev, error: errorMessage }))
      Alert.fire({
        icon: 'error',
        title: 'Verification Failed',
        text: errorMessage,
      })
    } finally {
      setWhatsappLinking((prev) => ({ ...prev, isLinking: false }))
    }
  }

  if (loading) {
    return <div>Loading...</div>
  }

  return (
    <div className="profile-container p-6">
      {/* Role Management Section */}
      <div className="mb-8">
        <RoleInfoSection userStatus={roleStatus.currentStatus} />
        <RoleRequestSection
          currentStatus={roleStatus?.currentStatus || 'STUDENT'}
          pendingApproval={roleStatus?.pendingApproval || false}
          pendingRoleId={roleStatus?.pendingRoleId || ''}
          onRequestRole={handleRequestRole}
          onCancelRequest={handleCancelRequest}
        />
      </div>

      {/* WhatsApp Integration Section */}
      <div className="card mt-4 p-4">
        <h3 className="text-xl font-semibold mb-4 flex items-center">
          <FontAwesomeIcon icon={faPhone} className="mr-2" />
          WhatsApp Integration
        </h3>

        {whatsappLinking.isLinked ? (
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-green-700">
              ✓ WhatsApp is linked to your account (
              {whatsappLinking.phoneNumber})
            </p>
            <p className="text-sm text-gray-600 mt-2">
              You can now receive course updates and interact with KET Academy
              via WhatsApp.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-gray-600">
              Link your WhatsApp account to receive course updates and interact
              with KET Academy directly through WhatsApp.
            </p>

            <div className="flex gap-4">
              <input
                type="tel"
                placeholder="WhatsApp number (e.g., +237679563542)"
                value={whatsappLinking.phoneNumber}
                onChange={(e) =>
                  setWhatsappLinking((prev) => ({
                    ...prev,
                    phoneNumber: e.target.value,
                  }))
                }
                className="flex-1 p-2 border rounded"
              />
              <button
                onClick={handleWhatsAppLinking}
                disabled={
                  whatsappLinking.isLinking || !whatsappLinking.phoneNumber
                }
                className="bg-blue-600 text-white px-4 py-2 rounded disabled:bg-gray-400"
              >
                {whatsappLinking.isLinking ? 'Linking...' : 'Link WhatsApp'}
              </button>
            </div>

            {whatsappLinking.error && (
              <p className="text-red-600 text-sm">{whatsappLinking.error}</p>
            )}

            <div className="mt-4">
              <input
                type="text"
                placeholder="Enter verification code"
                value={whatsappLinking.verificationCode}
                onChange={(e) =>
                  setWhatsappLinking((prev) => ({
                    ...prev,
                    verificationCode: e.target.value,
                  }))
                }
                className="w-full p-2 border rounded mb-2"
              />
              <button
                onClick={verifyWhatsAppCode}
                disabled={
                  whatsappLinking.isLinking || !whatsappLinking.verificationCode
                }
                className="bg-green-600 text-white px-4 py-2 rounded disabled:bg-gray-400"
              >
                Verify Code
              </button>
            </div>
          </div>
        )}

        {whatsappLinking.isLinked && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold mb-2">Available Commands:</h4>
            <ul className="list-disc list-inside space-y-1 text-gray-600">
              <li>Send "courses" to view your enrolled courses</li>
              <li>Send "progress" to check your course progress</li>
              <li>Send "help" to see all available commands</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

export default Profile
